import {
    type ChangeEvent,
    type FC,
    type RefObject,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react'

import { produce } from 'immer'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import * as spriteIcons from 'assets/svg/sprite'
import { IconClose, IconQuotes } from 'assets/svg/sprite'
import { Button } from 'components/Button/Button'
import { IconButton } from 'components/IconButton/IconButton'
import { addToast } from 'components/Toast/Toast'
import AudioMessage from 'components/UI/AudioMessage/AudioMessage'
import ConfirmModal from 'components/UI/Modal/Confirm/ConfirmModal'
import EditPreviewModal, {
  type ConfirmPayload
} from 'components/UI/Modal/EditPreviewModal/EditPreviewModal'
import SelectCategoriesModal from 'components/UI/Modal/SelectCategoriesModal/SelectCategoriesModal'
import { Modal } from 'components-ref/molecules/modal/modal'
import { useModalContext } from 'context/modalContext'
import GifPopup from 'features/GifPopup/GifPopup'
import PhotoEditView from 'features/PhotoEdit/PhotoEditView'
import { useChatCooldown } from 'helpers/chat/useChatCooldown'
import { fastFileHash } from 'helpers/hash/fastFileHash'
import { useSortableProgressChat } from 'helpers/hooks/chat/useSortableChatAttachments'
import { Icons } from 'helpers/icons'
import type { CloudFrontCredentials } from 'helpers/cloudfrontCookies'
import { validateMediaFiles } from 'helpers/media'
import {
  type ClientFrame,
  type ClientThumbs,
  generateClientThumbs,
  getExpectedClientThumbCount,
  revokeClientThumbs,
  validateClientThumbs
} from 'helpers/video/generateClientThumbs'
import { AddAnything } from 'pages/newPost/components/AddAnything'
import { PostImage } from 'pages/newPost/components/PostImage'
import { PostVideo } from 'pages/newPost/components/PostVideo'
import PostModal from 'pages/postModal'
import {
  abortVideoMultipart,
  attachAudio,
  attachPhotoFromS3,
  attachPreviewCoverFromS3,
  attachVideoFromS3,
  checkPhotoDupe,
  checkVideoDupe,
  completeVideoMultipart,
  getVideoThumbs,
  presignPhoto,
  startVideoMultipart,
  uploadFileToS3,
  uploadVideoMultipart
} from 'services/endpoints/attachment'
import { putRemoveDuplicates } from 'services/endpoints/settings/general/chat'
import { useAuthStore, useUserProfile } from 'store'
import { type ICategory, type IProgressInfo } from 'types/interfaces/ITypes'
import { type iNewMessage } from 'types/iTypes'
import { safeRandomUUID } from 'utils/safeRandomUUID'

import SetPrice from './components/SetPrice'
import WriteMessage from './components/WriteMessage'
import messageStyles from '../Message/_message.module.scss'

import './_directMessage.scss'


type VideoPreviewData = {
  defaultPreview: string
  videoId: string
  storageSrc: string
  cloudfront?: CloudFrontCredentials | null
  customPreviewCloudfront?: CloudFrontCredentials | null
  duration?: number
  orientation?: string
  thumbnails?: string[]
  thumbnailTimes?: Array<number | null>
  scrubFrames?: ClientFrame[]
  storyboard?: {
    manifest: string
    base_url: string
    thumbnail_width: number
    thumbnail_height: number
    tile_width: number
    tile_height: number
    interval: number
    sheet_count: number
    frame_count: number
  } | null
  thumbnailIndex?: number
  previewThumbnailIndex?: number | null
  customPreviewThumbKey?: string | null
  customPreviewThumbnailUrl?: string | null
  thumbsQueryDone?: boolean
  trimPreview?: { start: number; end: number }
  thumbType?: 'clip' | 'thumb'
  customThumbnailUrl?: string | null
  customThumbnailTime?: number | null
  customPreviewUrl?: string | null
  previewVideoId?: string | null
}

const CreateMessage: FC<{
    setMessageId?: (id: string | null) => void
    sendMessage: any
    customClass?: string
    disabled?: boolean
    writeMessageCustomClass?: string
    onGifClick?: (_val: any) => void
    reply?: { body: any; id: string | number; message: any; recieved?: boolean }
    setReply?: (
        _val: {
            body: string
            id: string | number
            message: any
            recieved?: boolean
        } | null
    ) => void
    forceHideAttachments?: boolean
    participants?: any
    selectedFiles: any[]
    setSelectedFiles: React.Dispatch<React.SetStateAction<any[]>>
    progressInfos: { val: IProgressInfo[] }
    setProgressInfos: React.Dispatch<
        React.SetStateAction<{ val: IProgressInfo[] }>
    >
    sending?: boolean
    hasGif?: boolean
    onFocusIn?: (_val: string) => void
    onFocusOut?: (_val: string) => void
    onMessageChange?: (_val: any) => void
    submitFn?: (_val: any) => void
    setGifOpenInfo?: (_val: boolean) => void
    inboxRef?: RefObject<HTMLDivElement>
    welcomeMessage?: boolean
    editWelcomeMessage?: boolean
    selectedRecipients?: string[] | null
    selectedGroupId?: string | null
    otherUserData?: any
    invalidRecipientReason?: 'empty-group' | 'no-audience' | null
    conversationId?: string | null
    messagesReady?: boolean
    onAttachmentsOpenChange?: (open: boolean) => void
    fanActionsOpen?: boolean
    onFanActionsOpenChange?: (open: boolean) => void
    keyboardOpen?: boolean
    sendingDisabled?: boolean
    
}> = ({
    sendMessage,
    customClass,
    writeMessageCustomClass,
    reply,
    forceHideAttachments,
    onGifClick,
    hasGif = false,
    setGifOpenInfo,
    onFocusIn,
    onFocusOut,
    onMessageChange,
    submitFn,
    setReply,
    disabled,
    sending,
    sendingDisabled = false,
    welcomeMessage,
    editWelcomeMessage,
    progressInfos,
    setProgressInfos,
    selectedFiles,
    setSelectedFiles,
    setMessageId,
    selectedRecipients,
    selectedGroupId,
    otherUserData,
    invalidRecipientReason,
    conversationId,
    messagesReady,
    onAttachmentsOpenChange,
    fanActionsOpen: externalFanActionsOpen,
    onFanActionsOpenChange,
}) => {
    const { t } = useTranslation()

    const progressInfosRef = useRef<any>(null)
    const [gifOpen, setGifOpen] = useState(false)
    const [priceModalActive, setPriceModalActive] = useState(false)
    const [isCategoriesOpen, setCategoriesOpen] = useState<boolean>(false)
    const [selectedCategories, setSelectedCateories] = useState<ICategory[]>([])
    const [attachmentsMinimized, setAttachmentsMinimized] = useState(false)
    const [schedule, setSchedule] = useState('')
    const replyRef = useRef<HTMLDivElement>(null)
    const attachmentsRef = useRef<HTMLDivElement>(null)
    const sendingRef = useRef(false)
    const [sendingLocal, setSendingLocal] = useState(false)
    const [previewOpen, setPreviewOpen] = useState(false)
    const location = useLocation()
    const isNewMessage = location.pathname === '/messages/new'
    const [previewMedia, setPreviewMedia] = useState<{
      postId: string
      post: any
    } | null>(null)
    const modalData = useModalContext()
    const [message, setMessage] = useState<iNewMessage>({
        text: '',
        audioMessage: null,
        audioPreview: null,
        price: 0,
        media: [],
        previewMedia: [],
        vaultImages: []
    })

const navigate = useNavigate()

const showRestrictedAccountModal = () => {
    modalData.addModal(
        'Restricted Account',
        <ConfirmModal
            body="Please contact support for any assistance."
            cancelText="Cancel"
            confirmText="Submit Ticket"
            cancelButtonWidth="13"
            confirmButtonWidth="16"
            cancelFn={() => {
                modalData.clearModal()
            }}
            confirmFn={() => {
                modalData.clearModal()
                navigate('/support/home')
            }}
        />
    )
}

  const {
      trySend,
      isChatCooldown,
  } = useChatCooldown()
  
    const [editPreviewModalOpen, setEditPreviewModalOpen] = useState(false)


    const [videoPreviewData, setVideoPreviewData] =
        useState<VideoPreviewData | null>(null)

    const [internalFanActionsOpen, setInternalFanActionsOpen] = useState(
        Boolean(welcomeMessage || editWelcomeMessage)
    )
    const fanActionsOpen = externalFanActionsOpen ?? internalFanActionsOpen

    const setFanActionsOpen = onFanActionsOpenChange ?? setInternalFanActionsOpen
    const userData = useUserProfile()!
    const modalContext = useModalContext()
    function openCategoriesModal() {
        setCategoriesOpen(true)
    }
    function closeCategoriesModal() {
        setCategoriesOpen(false)
    }

    const formatPrice = (price: number) => {
        return price % 1 === 0 ? price.toString() : price.toFixed(2);
    };
    const composerRef = useRef<HTMLDivElement | null>(null);
    const priceRef = useRef<number>(message.price);

    const attachmentsDropRef = useRef<HTMLDivElement | null>(null)
    const [hasDuplicateRecipients, setHasDuplicateRecipients] = useState(false)
    const [sendRemoveDuplicates, setSendRemoveDuplicates] = useState<boolean | null>(
        userData?.remove_duplicates ?? null
    )
  const [duplicateActionToast, setDuplicateActionToast] = useState<{
    key: string
    text: 'Send To All' | 'Send To New'
  } | null>(null)
  const duplicateActionToastTimeoutRef = useRef<number | null>(null)

  const isMultiRecipientSend = useMemo(() => {
    const hasGroup =
      typeof selectedGroupId === 'string' &&
      selectedGroupId.trim() !== ''

    const recipientCount = Array.isArray(selectedRecipients)
      ? selectedRecipients.filter(
          id =>
            typeof id === 'string' &&
            id.trim() !== ''
        ).length
      : 0

    return hasGroup || recipientCount > 1
  }, [selectedGroupId, selectedRecipients])

  const persistRemoveDuplicateSetting = async (value: boolean) => {
    if (!isMultiRecipientSend) return

    await putRemoveDuplicates({ remove_duplicates: value })

    // local state
    setSendRemoveDuplicates(value)

    // global cache (instant UI update everywhere)
    useAuthStore.getState().setUserProfile({
      remove_duplicates: value
    })
  }


const sendPreparedMessage = async (
    removeDuplicatesValue?: boolean | null
) => {
    if (
        userData?.status === 'restricted' &&
        Number(message.price) > 0
    ) {
        showRestrictedAccountModal()
        return
    }

    const cooldownResult = trySend()

    if (!cooldownResult.allowed) {
        return
    }
      if (sendingRef.current || sending || sendingLocal) return

      sendingRef.current = true
      setSendingLocal(true)

      try {
          const payload = prepareMessage(
              progressInfos.val,
              removeDuplicatesValue
          )

          await sendMessage(payload)

          resetAttachments()

          if (payload.schedule_date) {
              setSchedule('')
          }
      } catch (e) {
          console.error(e)
      } finally {
          sendingRef.current = false
          setSendingLocal(false)
      }
  }
// DDUPLICATE TOAST


const showDuplicateActionToast = (
  key: string,
  text: 'Send To All' | 'Send To New'
) => {
  if (duplicateActionToastTimeoutRef.current != null) {
    window.clearTimeout(duplicateActionToastTimeoutRef.current)
  }

  setDuplicateActionToast(null)

  requestAnimationFrame(() => {
    setDuplicateActionToast({ key, text })

    duplicateActionToastTimeoutRef.current = window.setTimeout(() => {
      setDuplicateActionToast((prev) => (prev?.key === key ? null : prev))
      duplicateActionToastTimeoutRef.current = null
    }, 1200)
  })
}

const handleToggleDuplicateBehavior = (
  itemKey: string,
  removeDuplicatesNext: boolean
) => {
  setSendRemoveDuplicates(removeDuplicatesNext)

  showDuplicateActionToast(
    itemKey,
    removeDuplicatesNext ? 'Send To All' : 'Send To New'
  )
}
const shouldShowDupeBorder = (
  hasExif: boolean
) => {
  return hasExif && !(isMultiRecipientSend && sendRemoveDuplicates === false)
}


const recomputeHasDuplicateRecipients = (items: any[]) => {
  const hasDupes = items.some((it) => {
    if (it.type === 'photo') return !!it.exif_media
    if (it.type === 'video') return !!it.exif_video_media
    return false
  })

  setHasDuplicateRecipients(hasDupes)
}

const rerunDupeChecksForExistingAttachments = useCallback(async () => {
    if (userData?.role === 'fan') {
      setHasDuplicateRecipients(false)
      return
    }
  const current = [...(progressInfosRef.current?.val ?? [])] as any[]

  const mediaItems = current.filter(
    (it) =>
      (it.type === 'photo' || it.type === 'video') &&
      (it.storageUrl || it.storage_path || it.previewUrl)
  )

  if (!mediaItems.length) {
    setHasDuplicateRecipients(false)
    return
  }

  const ctx =
    selectedGroupId != null
      ? { group_ids: [selectedGroupId] }
      : { recipients: selectedRecipients ?? undefined }

  const next = [...current]

  await Promise.all(
    mediaItems.map(async (item) => {
      const idx = next.findIndex((it) => String(it.id) === String(item.id))
      if (idx < 0) return

      try {
        if (item.type === 'photo') {
          let hash = item.hash_image as string | undefined

          if (!hash && item.file instanceof File) {
            hash = await fastFileHash(item.file)
          }

          if (!hash) {
            next[idx] = {
              ...next[idx],
              exif_media: null,
            }
            return
          }

          const dupe = await checkPhotoDupe({
            h: hash,
            size: item.size ?? 1,
            ...ctx,
          })

          if (dupe?.status === 'OK') {
            next[idx] = {
              ...next[idx],
              exif_media: dupe.data?.exif_media ?? null,
              duplicatePost: dupe.data?.post,
              hash_image: dupe.data?.image_hash ?? hash,
              storageUrl: dupe.data?.storage_path ?? next[idx].storageUrl,
              storage_path: dupe.data?.storage_path ?? next[idx].storage_path,
              orientation:
                dupe.data?.original_orientation ?? next[idx].orientation ?? null,
            }
          } else {
            next[idx] = {
              ...next[idx],
              exif_media: null,
              hash_image: hash,
            }
          }
        }

        if (item.type === 'video') {
          let hash = item.video_hash as string | undefined

          if (!hash && item.file instanceof File) {
            hash = await fastFileHash(item.file)
          }

          if (!hash) {
            next[idx] = {
              ...next[idx],
              exif_video_media: null,
            }
            return
          }

          const dupe = await checkVideoDupe({
            h: hash,
            size: item.size ?? 1,
            ...ctx,
          })

          if (dupe?.status === 'OK') {
            next[idx] = {
              ...next[idx],
              exif_video_media: dupe.data?.exif_media ?? null,
              duplicatePost: dupe.data?.post,
              video_hash: dupe.data?.video_hash ?? hash,
              hash_video: dupe.data?.video_hash ?? hash,
              storageUrl:
                dupe.data?.storage_path ?? dupe.data?.path ?? next[idx].storageUrl,
              orientation:
                dupe.data?.original_orientation ??
                dupe.data?.orientation ??
                next[idx].orientation,
              duration: dupe.data?.duration ?? next[idx].duration,
            }
          } else {
            next[idx] = {
              ...next[idx],
              exif_video_media: null,
              video_hash: hash,
              hash_video: hash,
            }
          }
        }
      } catch (err: any) {
        if (err?.response?.status === 404) {
          if (item.type === 'photo') {
            next[idx] = {
              ...next[idx],
              exif_media: null,
            }
          }

          if (item.type === 'video') {
            next[idx] = {
              ...next[idx],
              exif_video_media: null,
            }
          }

          return
        }

        console.error('rerun dupe check failed', err)
      }
    })
  )

  syncProgress(next)
  recomputeHasDuplicateRecipients(next)
}, [
  selectedGroupId,
  selectedRecipients,
  userData?.role
])


// ABORT/CANCEL/REMOVE ATTACHMENT LOGIC

const uploadOpsRef = useRef<
  Record<
    string,
    {
      aborted: boolean
      controller: AbortController
      stage: 'idle' | 'presign' | 'hash' | 'dupe' | 'upload' | 'attach' | 'done'
      cancelCurrent?: () => void
    }
  >
>({})

const ensureUploadOp = (clientId: string) => {
  if (!uploadOpsRef.current[clientId]) {
    uploadOpsRef.current[clientId] = {
      aborted: false,
      controller: new AbortController(),
      stage: 'idle',
      cancelCurrent: undefined,
    }
  }
  return uploadOpsRef.current[clientId]
}

const setUploadStage = (
  clientId: string,
  stage: NonNullable<typeof uploadOpsRef.current[string]>['stage']
) => {
  const op = ensureUploadOp(clientId)
  op.stage = stage
}

const abortUploadOp = (clientId: string) => {
  const op = uploadOpsRef.current[clientId]
  if (!op) return

  op.aborted = true

  try {
    op.cancelCurrent?.()
  } catch {}

  try {
    op.controller.abort()
  } catch {}
}

const clearUploadOp = (clientId: string) => {
  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
  delete uploadOpsRef.current[clientId]
}

const isUploadAborted = (clientId: string) => {
  return !!uploadOpsRef.current[clientId]?.aborted
}

const abortAllUploadOps = () => {
  Object.keys(uploadOpsRef.current).forEach((id) => {
    abortUploadOp(id)
  })
}

const throwIfUploadAborted = (clientId: string) => {
  if (isUploadAborted(clientId)) {
    const err = new Error('__UPLOAD_ABORTED__')
    ;(err as any).code = '__UPLOAD_ABORTED__'
    throw err
  }
}

// REMOVE ATTACHMENT LOGIC ABOVE


useEffect(() => {
  return () => {
    if (duplicateActionToastTimeoutRef.current != null) {
      window.clearTimeout(duplicateActionToastTimeoutRef.current)
    }
  }
}, [])

useEffect(() => {
  progressInfosRef.current = progressInfos
}, [progressInfos])

useEffect(() => {
  if (!isMultiRecipientSend) {
    const current = [...(progressInfosRef.current?.val ?? [])] as any[]
    const cleared = current.map((it) => {
      if (it.type === 'photo') {
        return { ...it, exif_media: null }
      }

      if (it.type === 'video') {
        return { ...it, exif_video_media: null }
      }

      return it
    })

    syncProgress(cleared)
    setHasDuplicateRecipients(false)
    return
  }

  const hasMedia = (progressInfosRef.current?.val ?? []).some(
    (it: any) => it.type === 'photo' || it.type === 'video'
  )

  if (!hasMedia) return

  void rerunDupeChecksForExistingAttachments()
}, [isMultiRecipientSend, selectedGroupId, selectedRecipients, rerunDupeChecksForExistingAttachments])


const sortableItems = progressInfos.val.filter(
  (i) => i.type === 'photo' || i.type === 'video'
)

const attachmentsOpen =
  selectedFiles.some((f: any) => !f.type?.includes?.('audio')) &&
  !attachmentsMinimized

const { getId } = useSortableProgressChat({
  dropRef: attachmentsDropRef,
  itemCount: sortableItems.length,
  onSort: (ids) => {
    // 1) build lookup from current list
    const cur = (progressInfosRef.current?.val ?? progressInfos.val) as any[]

    // 2) only reorder photo+video based on ids
    const byId = new Map<string, any>()
    for (const it of cur) {
      if (it.type === 'photo' || it.type === 'video') {
        byId.set(getId(it), it)
      }
    }

    const nextSorted = ids
      .map((id) => byId.get(id))
      .filter(Boolean)

    // 3) keep non-sortables (audio/other) at end (or wherever you want)
    const rest = cur.filter((i) => i.type !== 'photo' && i.type !== 'video')

    const merged = [...nextSorted, ...rest]

    syncProgress(merged as any)
    setSelectedFiles(merged as any)
  },
})
    useEffect(() => {
    priceRef.current = message.price;
    }, [message.price]);

  const selectFiles = async (
    event: ChangeEvent<HTMLInputElement>,
    groupId?: string | null,
    recipients?: string[] | null
  ) => {
    const input = event.target;
    const tmpFiles = input.files;

    if (!tmpFiles?.length) {
        // important: reset so re-selecting same file works
        input.value = '';
        return;
    }

    const files = await validateMediaFiles(Array.from(tmpFiles), t);

    const audioFiles = files.filter(file => file.type.includes('audio/'));
    const prevAudioFiles = progressInfos.val.filter(
        info => info.type === 'sound' || info.type === 'audio'
    );
    const videoPhotoFiles = files.filter(file =>
        file.type.includes('video/') || file.type.includes('image/')
    );

    const tmpVideoPhotoFiles = videoPhotoFiles.filter(file => {
        const res = !selectedFiles.some(el => el.size === file.size);
        if (!res) addToast('error', t('error:cannotUploadDuplicateFile'));
        return res;
    });

    if (
        audioFiles.length > 1 ||
        (audioFiles.length >= 1 && prevAudioFiles.length === 1)
    ) {
        addToast('error', t('youCanUploadOnlyOneAudioFilePerPost'));
        input.value = ''; // reset before bailing
        return;
    }

    if (files.length + progressInfos.val.length > 40) {
        addToast('error', t('maximumNumberOfMediaFilesExceeded'));
        input.value = ''; // reset before bailing
        return;
    }

    const effectiveGroupId = groupId ?? selectedGroupId;
    const effectiveRecipients = recipients ?? selectedRecipients;

    const allFiles =
        audioFiles.length > 0 && prevAudioFiles.length === 0
        ? [...tmpVideoPhotoFiles, audioFiles[0]]
        : tmpVideoPhotoFiles;

    uploadFiles(allFiles, effectiveGroupId, effectiveRecipients);
    setSelectedFiles([...selectedFiles, ...tmpVideoPhotoFiles]);

    input.value = '';
    };

    useEffect(() => {
    onAttachmentsOpenChange?.(attachmentsOpen)
    }, [attachmentsOpen, onAttachmentsOpenChange])

  const resetAttachments = () => {
      abortAllUploadOps()

      syncProgress([])
      setSelectedFiles([])

      setHasDuplicateRecipients(false)
      setSchedule('')

      setMessage({
          text: '',
          audioMessage: null,
          audioPreview: null,
          price: 0,
          media: [],
          previewMedia: [],
          vaultImages: [],
      })
  }
const handleSendMessage = async () => {
    if (
        userData?.status === 'restricted' &&
        Number(message.price) > 0
    ) {
        showRestrictedAccountModal()
        return
    }

    if (invalidRecipientReason) {
          modalData.addModal(
              t('groupHasNoUsers'),
              <ConfirmModal
                  confirmButtonWidth="14"
                  body={t('groupHasNoUsersDescription')}
                  confirmText={t('ok')}
                  confirmFn={() => modalData.clearModal()}
              />
          )
          return
      }

      const shouldShowModal =
          isMultiRecipientSend &&
          hasDuplicateRecipients &&
          sendRemoveDuplicates == null

      if (shouldShowModal) {
        modalData.addModal(
          'Duplicate Recipients',
          <ConfirmModal
            body={
              <>
                Some attachments have been seen by your recipients.
                <br />
                <br />
                Sending to New Recipients will automatically remove attachments the user has received.
              </>
            }
            cancelText='Send to All'
            confirmText='Send To New'
            showRememberSetting
            rememberSettingLabel='Remember my setting'
            cancelButtonWidth='13'
            confirmButtonWidth='16'
            cancelFn={async (remember) => {
              if (remember) {
                await persistRemoveDuplicateSetting(false)
              }

              await sendPreparedMessage(false)
            }}
            confirmFn={async (remember) => {
              if (remember) {
                await persistRemoveDuplicateSetting(true)
              }

              await sendPreparedMessage(true)
            }}
          />
        )

        return
      }

      await sendPreparedMessage()
  }

  const removeFile = (index: number) => {
    const item: any = progressInfosRef.current?.val?.[index] ?? progressInfos.val[index]
    if (!item) return

    abortUploadOp(String(item.id))
    revokeIfBlob(item?.previewUrl)

    const currentList = (progressInfosRef.current?.val ?? progressInfos.val) as any[]
    const tmpProgressInfos = currentList.filter((_, i) => i !== index)

    // cancel any pending batched sync so stale state cannot re-apply
    if (syncRafRef.current != null) {
      cancelAnimationFrame(syncRafRef.current)
      syncRafRef.current = null
    }
    progressNextRef.current = null

    // IMMEDIATE commit
    progressInfosRef.current = { val: tmpProgressInfos }
    setProgressInfos({ val: tmpProgressInfos })

    setSelectedFiles(prev =>
      prev.filter((f: any) => f?.name !== item?.fileName && f?.previewUrl !== item?.previewUrl)
    )
  }

    useEffect(() => {
    return () => {
        const list = progressInfosRef.current?.val ?? [];
        for (const it of list) revokeIfBlob(it?.previewUrl);
    };
    }, []);

    const handleToggleMediaLocked = (idx: number) => {
      const updatedProgressInfos = [...progressInfos.val]
      const updatedSelectedFiles = [...selectedFiles]
      const currentItem = updatedProgressInfos[idx]
      const isCurrentlyLocked = !!currentItem.locked

      if (message.price && isCurrentlyLocked) {
        const lockedCount = updatedProgressInfos.filter(item => item.locked).length
        if (lockedCount === 1) {
          return
        }
      }

      updatedProgressInfos[idx] = {
        ...currentItem,
        locked: !isCurrentlyLocked,
        lockTouched: true,
      }

      const isNowUnlocked = isCurrentlyLocked

      if (isNowUnlocked && idx > 0) {
        const everyOtherItemLocked = updatedProgressInfos.every((item, i) =>
          i === idx ? !item.locked : !!item.locked
        )

        if (everyOtherItemLocked) {
          const [movedInfo] = updatedProgressInfos.splice(idx, 1)
          const [movedFile] = updatedSelectedFiles.splice(idx, 1)
          updatedProgressInfos.unshift(movedInfo)
          updatedSelectedFiles.unshift(movedFile)
        }
      }

      syncProgress(updatedProgressInfos)
      setSelectedFiles(updatedSelectedFiles)
    }
    
    // keep state + ref in sync in one place
    const progressNextRef = useRef<IProgressInfo[] | null>(null);
    const syncRafRef = useRef<number | null>(null);

    const syncProgress = (next: IProgressInfo[]) => {
    progressNextRef.current = next;

    if (syncRafRef.current != null) return;

    syncRafRef.current = requestAnimationFrame(() => {
        syncRafRef.current = null;

        const finalNext = progressNextRef.current ?? [];
        progressNextRef.current = null;

        setProgressInfos({ val: finalNext });
        progressInfosRef.current = { val: finalNext };
    });
    };

useEffect(() => {
    if (welcomeMessage || editWelcomeMessage) {
        setInternalFanActionsOpen(true)
    }
}, [welcomeMessage, editWelcomeMessage])

const appendAttachments = useCallback(async (items: any[]) => {
  if (!items?.length) return

  const current = (
    progressInfosRef.current?.val ??
    progressInfos.val
  ) as any[]

  const appendedItems = [...current, ...items]

  // Show selected Vault media immediately.
  syncProgress(appendedItems)

  setSelectedFiles(previous => [
    ...previous,
    ...items
  ])

  const videoIds = Array.from(
    new Set(
      items
        .filter((item: any) => item?.type === 'video')
        .map((item: any) => {
          const id = item.video_id ?? item.id
          return typeof id === 'string' ? id : ''
        })
        .filter(Boolean)
    )
  )

  if (!videoIds.length) {
    return
  }

  try {
    const response = await getVideoThumbs(videoIds)

    const rows =
      response?.data?.data ??
      response?.data ??
      []

    if (!Array.isArray(rows)) {
      return
    }

  const videoThumbDataById = new Map<
    string,
    {
      thumbs: {
        src: string
        active?: boolean
        time?: number | null
        type?: string
      }[]
      storyboard?: VideoPreviewData['storyboard']
    }
  >()

    rows.forEach((row: any) => {
      const videoId =
        row?.id ??
        row?.video_id

      if (
        typeof videoId !== 'string' ||
        !videoId ||
        !Array.isArray(row?.thumbs)
      ) {
        return
      }

      videoThumbDataById.set(videoId, {
        thumbs: row.thumbs,
        storyboard: row.storyboard ?? null
      })
    })

    const latestItems = (
      progressInfosRef.current?.val ??
      appendedItems
    ) as any[]

    const hydratedItems = latestItems.map(item => {
      if (item?.type !== 'video') {
        return item
      }

      const videoId =
        item.video_id ??
        item.id

      if (
        typeof videoId !== 'string' ||
        !videoId
      ) {
        return item
      }

      const videoThumbData =
        videoThumbDataById.get(videoId)

      if (!videoThumbData?.thumbs?.length) {
        return item
      }

      const thumbRows = videoThumbData.thumbs

      const thumbnails = thumbRows
        .map(thumb => thumb?.src)
        .filter(
          (src): src is string =>
            typeof src === 'string' &&
            src.length > 0
        )

      const thumbnailTimes = thumbRows.map(
        thumb =>
          thumb.time != null &&
          Number.isFinite(Number(thumb.time))
            ? Number(thumb.time)
            : null
      )

      const activeThumbnailIndex =
        thumbRows.findIndex(
          thumb => thumb.active
        )

      const resolvedActiveIndex =
        activeThumbnailIndex >= 0
          ? activeThumbnailIndex
          : 0

      return {
        ...item,
        video_id: videoId,
        thumbnails,
        thumbnailTimes,
        storyboard:
          videoThumbData.storyboard ?? null,
        activeThumbnailIndex:
          resolvedActiveIndex,
        imagePreview:
          item.imagePreview ??
          thumbnails[resolvedActiveIndex] ??
          thumbnails[0] ??
          null,
        thumbsDone: true,
        thumbsQueryDone: true,
        thumbTarget: thumbnails.length,
        thumbs_from: 'server'
      }
    })

    syncProgress(hydratedItems)
  } catch (error) {
    console.error(
      '[CreateMessage] Failed to load Vault video thumbnails',
      error
    )
  }
}, [
  progressInfos.val,
  setSelectedFiles
])

    useEffect(() => {
    return () => {
        if (syncRafRef.current != null) cancelAnimationFrame(syncRafRef.current);
    };
    }, []);

    const revokeIfBlob = (url?: string) => {
    if (url && url.startsWith('blob:')) {
        try { URL.revokeObjectURL(url); } catch {}
    }
    };

    const handleTogglePreviewOn = (idx: number) => {
        const tmpArr = progressInfos.val.map((info, index) => {
            if (index === idx) {
                return {
                    ...info,
                    previewOn: !info.previewOn
                }
            } else {
                return info
            }
        })
        syncProgress(tmpArr)
    }

    const getPreviewStatus = (status: string) => {
        if (status === 'no-preview') {
            return false
        }
        return true
    }

const handleEditVideoPreview = (
  defaultPreview: string,
  videoId: string,
  storageSrc: string,
  duration?: number,
  orientation?: string,
  thumbnails?: string[],
  thumbnailTimes?: Array<number | null>,
  imagePreview?: string,
  trimPreview?: { start: number; end: number },
  thumbType: 'clip' | 'thumb' = 'thumb',
  customThumbnailUrl?: string | null,
  customThumbnailTime?: number | null,
  customPreviewUrl?: string | null,
  previewVideoId?: string | null,
  activeThumbnailIndex?: number | null,
  thumbsQueryDone = true,
  scrubFrames?: ClientFrame[],
  previewThumbnailIndex?: number | null,
  customPreviewThumbKey?: string | null,
  customPreviewThumbnailUrl?: string | null,
  storyboard?: VideoPreviewData['storyboard'],
  cloudfront?: CloudFrontCredentials | null,
  customPreviewCloudfront?: CloudFrontCredentials | null
) => {
  const normalizedCustomThumbnailTime =
    customThumbnailTime != null &&
    Number.isFinite(Number(customThumbnailTime))
      ? Number(customThumbnailTime)
      : null

  const hasCustomThumbnail =
    typeof customThumbnailUrl === 'string' &&
    customThumbnailUrl.trim() !== '' &&
    normalizedCustomThumbnailTime !== null

  let thumbnailIndex: number | undefined

  if (
    !hasCustomThumbnail &&
    typeof activeThumbnailIndex === 'number' &&
    thumbnails?.length
  ) {
    thumbnailIndex = activeThumbnailIndex
  } else if (
    !hasCustomThumbnail &&
    imagePreview &&
    thumbnails?.length
  ) {
    const index = thumbnails.indexOf(imagePreview)

    if (index >= 0) {
      thumbnailIndex = index
    }
  }

  setVideoPreviewData({
    defaultPreview,
    videoId,
    storageSrc,
    cloudfront,
    customPreviewCloudfront,
    duration,
    orientation,
    thumbnails,
    thumbnailTimes,
    trimPreview,
    thumbnailIndex,
    thumbType,
    thumbsQueryDone,
    customThumbnailUrl:
      hasCustomThumbnail
        ? customThumbnailUrl
        : null,
    customThumbnailTime:
      hasCustomThumbnail
        ? normalizedCustomThumbnailTime
        : null,
    customPreviewUrl:
      customPreviewUrl ?? null,
    previewVideoId:
      previewVideoId ?? null,
    scrubFrames,
    previewThumbnailIndex:
      previewThumbnailIndex ?? null,
    customPreviewThumbKey:
      customPreviewThumbKey ?? null,
    customPreviewThumbnailUrl:
      customPreviewThumbnailUrl ?? null,
    storyboard:
      storyboard ?? null
  })

  setEditPreviewModalOpen(true)
}

useLayoutEffect(() => {
  const apply = () => {
    const dropzoneHeight =
      attachmentsDropRef.current && attachmentsOpen
        ? Math.ceil(attachmentsDropRef.current.getBoundingClientRect().height)
        : 0

    document.documentElement.style.setProperty(
      '--attachments-h',
      `${dropzoneHeight}px`
    )

    document.documentElement.style.setProperty(
      '--fan-actions-h',
      fanActionsOpen ? '3rem' : '0px'
    )
    document.documentElement.style.setProperty(
      '--attachments-extra-h',
      attachmentsOpen ? '7rem' : '0px'
    )
  }

  apply()

  const ro = new ResizeObserver(apply)

  if (attachmentsDropRef.current) {
    ro.observe(attachmentsDropRef.current)
  }

  return () => ro.disconnect()
}, [
  attachmentsOpen,
  fanActionsOpen,
  sortableItems.length,
  selectedFiles.length,
])

    const handleUpdatePreview = (
      data: ConfirmPayload
    ) => {
      const currentList = (
        progressInfosRef.current?.val ??
        progressInfos.val
      ) as IProgressInfo[]

      const nextList = produce(
        currentList,
        draft => {
          const item = draft.find(
            info =>
              info.previewUrl ===
              data.defaultPrev
          ) as any

          if (!item) return

          if (data.activeThumbnail) {
            item.imagePreview =
              data.activeThumbnail
          }

          item.thumbType =
            data.thumbType

          const selectedCustomThumbnail =
            typeof data.selectedTime === 'number'

          const selectedGeneratedThumbnail =
            typeof data.selectedIndex === 'number'

          const selectedUploadedThumbnail =
            typeof data.customThumbKey === 'string' &&
            data.customThumbKey.length > 0

          if (selectedUploadedThumbnail) {
            item.custom_thumb_key =
              data.customThumbKey
            item.custom_thumb_crop =
              data.customThumbCrop ?? null
            item.custom_thumbnail = null
            item.customThumbnailUrl = null
            item.activeThumbnailIndex = null
          } else if (selectedCustomThumbnail) {
            item.custom_thumbnail =
              Number(data.selectedTime)
            item.customThumbnailUrl =
              data.activeThumbnail ?? null
            item.custom_thumb_key = null
            item.custom_thumb_crop = null
            item.activeThumbnailIndex = null
          } else if (selectedGeneratedThumbnail) {
            item.activeThumbnailIndex =
              Number(data.selectedIndex)
            item.custom_thumbnail = null
            item.customThumbnailUrl = null
            item.custom_thumb_key = null
            item.custom_thumb_crop = null
          } else if (
            data.activeThumbnail &&
            Array.isArray(item.thumbnails)
          ) {
            const derivedIndex =
              item.thumbnails.indexOf(
                data.activeThumbnail
              )

            if (derivedIndex >= 0) {
              item.activeThumbnailIndex =
                derivedIndex
              item.custom_thumbnail = null
              item.customThumbnailUrl = null
              item.custom_thumb_key = null
              item.custom_thumb_crop = null
            }
          }

          if (data.customPreviewThumbKey) {
            item.previewThumbIndex = null
            item.previewThumbKey =
              data.customPreviewThumbKey
            item.previewThumbUrl =
              data.customPreviewThumbnailUrl ??
              null
          } else if (
            typeof data.previewThumbnailIndex ===
            'number'
          ) {
            item.previewThumbIndex =
              data.previewThumbnailIndex
            item.previewThumbKey = null
            item.previewThumbUrl =
              item.thumbnails?.[
                data.previewThumbnailIndex
              ] ?? null
          } else {
            item.previewThumbIndex = null
            item.previewThumbKey = null
            item.previewThumbUrl = null
          }

          item.preview_video_id =
            data.previewVideoId ?? null

          item.customPreviewUrl =
            data.customPreviewUrl ?? null

          item.trimPreview =
            !data.previewVideoId &&
            data.thumbType === 'clip' &&
            data.clip
              ? {
                  start: data.clip.start,
                  end: data.clip.end
                }
              : undefined
        }
      )

      syncProgress(nextList)
      setEditPreviewModalOpen(false)
    }
    useEffect(() => {
      setSendRemoveDuplicates(userData?.remove_duplicates ?? null)
    }, [userData?.remove_duplicates])

    useEffect(() => {
        if (localStorage.getItem('share-message')) {
            const sharedPostArray = localStorage.getItem('share-message')
            const parsedArray = sharedPostArray && JSON.parse(sharedPostArray)

            const _progressInfos = progressInfosRef.current
                ? [...progressInfosRef?.current?.val]
                : []
            for (let i = 0; i < parsedArray.length; i++) {
                if (parsedArray[i].post) {
                    if (parsedArray[i].post.photos) {
                        const photos = parsedArray[i].post.photos
                        photos.forEach((photo: any) => {
                            _progressInfos.push({
                                id: photo.id,
                                cancel: () => {},
                                error: '',
                                fileName: `shared-photo-${photo.id}`,
                                locked: false,
                                orientation: photo.orientation,
                                percentage: 100,
                                previewUrl: photo.url,
                                storageUrl: photo.url,
                                type: 'photo',
                                text: photo.text
                            })
                        })
                    }
                    if (parsedArray[i].post.videos.length > 0) {
                        const videos = parsedArray[i].post.videos
                        videos.forEach((video: any) => {
                            _progressInfos.push({
                                id: video.id,
                                cancel: () => {},
                                error: '',
                                fileName: `shared-video-${video.id}`,
                                locked: false,
                                orientation: video.orientation,
                                percentage: 100,
                                previewUrl: video.url,
                                storageUrl: video.url,
                                cloudfront: video.cloudfront ?? null,
                                customPreviewCloudfront:
                                  video.preview_cloudfront ??
                                  video.preview?.cloudfront ??
                                  null,
                                type: 'video',
                                previewOn: false,
                                thumbnails: video.thumbnails || []
                            })
                        })
                    }
                }
                if (parsedArray[i].story) {
                    const story = parsedArray[i].story
                    if (story.image_src) {
                        _progressInfos.push({
                            id: story.id,
                            cancel: () => {},
                            error: '',
                            fileName: `shared-story-photo-${story.id}`,
                            locked: false,
                            orientation: story.image_orientation,
                            percentage: 100,
                            previewUrl: story.image_src,
                            storageUrl: story.image_src,
                            type: 'photo',
                            text: story.text
                        })
                    }
                    if (story.video_src) {
                        _progressInfos.push({
                            id: story.id,
                            cancel: () => {},
                            error: '',
                            fileName: `shared-story-video-${story.id}`,
                            locked: false,
                            orientation: story.video_orientation,
                            percentage: 100,
                            previewUrl: story.video_src,
                            storageUrl: story.video_src,
                            cloudfront: story.video_cloudfront ?? null,
                            type: 'video',
                            thumbnails: story.thumbnails || [],
                            previewOn: false
                        })
                    }
                }
                if (parsedArray[i].message) {
                    if (parsedArray[i].message.photos) {
                        const photos = parsedArray[i].message.photos
                        photos.forEach((photo: any) => {
                            _progressInfos.push({
                                id: photo.id,
                                cancel: () => {},
                                error: '',
                                fileName: `shared-photo-${photo.id}`,
                                locked: false,
                                orientation: photo.orientation,
                                percentage: 100,
                                previewUrl: photo.url,
                                storageUrl: photo.url,
                                type: 'photo',
                                text: photo.text
                            })
                        })
                    }
                    if (parsedArray[i].message.videos.length > 0) {
                        const videos = parsedArray[i].message.videos
                        videos.forEach((video: any) => {
                            _progressInfos.push({
                                id: video.id,
                                cancel: () => {},
                                error: '',
                                fileName: `shared-video-${video.id}`,
                                locked: false,
                                orientation: video.orientation,
                                percentage: 100,
                                previewUrl: video.url,
                                storageUrl: video.url,
                                cloudfront: video.cloudfront ?? null,
                                customPreviewCloudfront:
                                  video.preview_cloudfront ??
                                  video.preview?.cloudfront ??
                                  null,
                                type: 'video',
                                previewOn: false,
                                thumbnails: video.thumbnails || []
                            })
                        })
                    }
                }
            }

            setSelectedFiles([..._progressInfos])

            setProgressInfos({ val: _progressInfos })
            progressInfosRef.current = {
                val: _progressInfos
            }

            localStorage.removeItem('share-message')
        }
    }, [])

const uploadCustomVideoPreview = async (file: File): Promise<string> => {
  const meta = await import('helpers/video/readClientMeta')
    .then(async ({ readClientVideoMeta }) => await readClientVideoMeta(file))
    .catch(() => null)

  const duration = meta?.duration || 0
  const orientation = meta?.orientation

  const multipart = await startVideoMultipart({
    type: 'post_video',
    contentType: file.type,
    size: file.size
  })

  const { key, uploadId, partSize, totalParts, parts } = multipart

  try {
    const completedParts = await uploadVideoMultipart({
      file,
      key,
      uploadId,
      partSize,
      totalParts,
      parts,
      contentType: file.type,
      concurrency: 4,
      setProgress: () => {}
    })

    if (!completedParts?.length) {
      throw new Error('Preview video upload did not complete.')
    }

    await completeVideoMultipart({
      key,
      uploadId,
      parts: completedParts
    })

    const response = await attachVideoFromS3({
      s3_key: key,
      duration: duration > 0 ? Number(duration.toFixed(2)) : null,
      orientation: orientation ?? null,
      preview: true,
      is_preview_video: true,
      type: 'message'
    } as any)

    const data = response?.data?.data ?? response?.data ?? response

    if (typeof data?.id !== 'string' || !data.id) {
      throw new Error('Preview video did not attach.')
    }

    return data.id
  } catch (error) {
    try {
      await abortVideoMultipart({ key, uploadId })
    } catch {}

    throw error
  }
}

const uploadCustomVideoThumbnail = async (
  file: File,
  videoId: string
): Promise<string> => {
  const ext =
    file.name.split('.').pop() ?? 'jpg'

  const s3Key =
    `public/images/originals/${safeRandomUUID()}.${ext}`

  const {
    url: putUrl,
    key
  } = await presignPhoto({
    contentType: file.type,
    key: s3Key,
    type: 'story_image',
    size: file.size
  })

  await uploadFileToS3(
    putUrl,
    file,
    () => {}
  )

  await attachPreviewCoverFromS3({
    video_id: videoId,
    s3_key: key,
    expected_bytes: file.size
  })

  return key
}

const uploadCustomPreviewThumbnail = async (
  file: File,
  videoId: string
): Promise<string> => {
  const { url: putUrl, key } = await presignPhoto({
    type: 'preview_cover',
    contentType: file.type,
    size: file.size
  })

  await uploadFileToS3(
    putUrl,
    file,
    () => {}
  )

  const response =
    await attachPreviewCoverFromS3({
      video_id: videoId,
      s3_key: key,
      expected_bytes: file.size
    })

  const data =
    response?.data?.data ??
    response?.data ??
    response

  if (
    typeof data?.key !== 'string' ||
    !data.key
  ) {
    throw new Error(
      'Preview cover did not attach.'
    )
  }

  return data.key
}


const revokeClientThumbAssets = (entry: any) => {
  if (Array.isArray(entry.thumbnails)) {
    entry.thumbnails.forEach((url: string) => {
      if (url?.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(url)
        } catch {}
      }
    })
  }

  if (Array.isArray(entry.scrubFrames)) {
    entry.scrubFrames.forEach((frame: ClientFrame) => {
      if (frame?.url?.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(frame.url)
        } catch {}
      }
    })
  }
}

const applyGeneratedClientThumbs = (
  entry: any,
  generated: ClientThumbs
) => {
  entry.thumbnails = generated.thumbs.map(
    thumb => thumb.url
  )

  entry.thumbnailTimes = generated.thumbs.map(
    thumb => thumb.time
  )

  entry.scrubFrames = generated.scrubFrames

  entry.activeThumbnailIndex = 0

  entry.imagePreview =
    generated.thumbs[0]?.url ??
    entry.imagePreview ??
    null

  entry.thumbTarget =
    generated.thumbs.length

  entry.thumbsDone =
    generated.thumbs.length > 0

  entry.thumbsQueryDone = true

  entry.thumbs_from = 'client'
}



const upload = useCallback(
  async (
    file: File,
    selectedGroupId?: string | null,
    selectedRecipients?: string[] | null,
    localId?: string
  ) => {
    const findIndex = (list: any[]) =>
      localId
        ? list.findIndex((it: any) => it.id === localId)
        : list.findIndex((it: any) => it.fileName === file.name)

    const clientId = String(localId ?? file.name)
    const op = ensureUploadOp(clientId)
    const signal = op.controller.signal

    const safeSyncById = (updater: (entry: any) => void) => {
      if (isUploadAborted(clientId)) return

      const list = [...(progressInfosRef.current?.val ?? [])]
      const idx = findIndex(list)

      if (idx >= 0) {
        updater(list[idx])
        syncProgress(list as any)
      }
    }

    try {
      // -------------------------
      // PHOTO
      // -------------------------
      if (file.type.includes('image')) {
        setUploadStage(clientId, 'presign')

        const ext = file.name.split('.').pop() ?? 'jpg'
        const s3Key = `public/images/originals/${safeRandomUUID()}.${ext}`

        const { url: putUrl, key } = await presignPhoto(
          {
            contentType: file.type,
            key: s3Key,
            type: 'story_image',
            size: file.size
          },
          signal
        )
        throwIfUploadAborted(clientId)

        setUploadStage(clientId, 'hash')
        const fileHash = await fastFileHash(file)
        throwIfUploadAborted(clientId)

        if (userData?.role !== 'fan') {
          setUploadStage(clientId, 'dupe')

          const ctx =
            selectedGroupId != null
              ? { group_ids: [selectedGroupId] }
              : { recipients: selectedRecipients ?? undefined }

          const dupe = await checkPhotoDupe(
            {
              h: fileHash,
              size: file.size,
              ...ctx,
            },
            signal
          )

          throwIfUploadAborted(clientId)

          if (dupe?.status === 'OK') {
            if (isMultiRecipientSend) {
              setHasDuplicateRecipients(true)
            }

            safeSyncById((it) => {
              const d: any = dupe.data ?? {}

              it.type = 'photo'
              it.processing = false
              it.percentage = 100
              it.error = ''

              it.photo_id = d.id
              it.storageUrl = d.path ?? null
              it.thumb_path = d.thumb_path ?? null
              it.url = d.url ?? null
              it.thumb_url = d.thumb_url ?? null

              if (!it.lockTouched) {
                it.locked = !!priceRef.current
              }

              it.exif_media = d.exif_media ?? null
              it.duplicatePost = d.post

              it.orientation =
                d.original_orientation ??
                it.orientation ??
                null

              it.hash_image =
                d.image_hash ??
                fileHash

              it.is_duplicate = true
            })

            setUploadStage(clientId, 'done')
            clearUploadOp(clientId)

            return null
          }
        }

        setUploadStage(clientId, 'upload')
        await uploadFileToS3(
          putUrl,
          file,
          (loaded, total, cancelCb) => {
            const op = ensureUploadOp(clientId)
            op.cancelCurrent = cancelCb

            if (isUploadAborted(clientId)) {
              try { cancelCb?.() } catch {}
              return
            }

            const cur = [...(progressInfosRef.current?.val ?? [])]
            const idx = findIndex(cur)
            if (idx >= 0) {
              const it: any = cur[idx]
              const perc = Math.round((100 * loaded) / (total || file.size || 1))
              it.percentage = perc
              it.cancel = cancelCb
              it.error = ''
              it.type = 'photo'
              it.processing = true
              syncProgress(cur as any)
            }
          },
          signal
        )
        throwIfUploadAborted(clientId)

        setUploadStage(clientId, 'attach')
        const res = await attachPhotoFromS3(
          {
            s3_key: key,
            type: 'message',
            group_id: selectedGroupId ?? undefined,
            recipients: selectedRecipients ?? undefined,
            hash_image: fileHash
          },
          signal
        )
        throwIfUploadAborted(clientId)

        const resData =
          (res as any)?.data?.data ??
          (res as any)?.data ??
          res

        safeSyncById((it) => {
          it.storageUrl =
            resData.path ??
            null

          if (!it.lockTouched) {
            it.locked = !!priceRef.current
          }

          it.exif_data = resData.exif_data
          it.exif_media = resData.exif_media

          it.orientation =
            resData.orientation ??
            resData.original_orientation ??
            it.orientation ??
            null

          it.photo_id =
            resData.id

          it.hash_image =
            resData.image_hash ??
            resData.hash_image ??
            fileHash

          it.processing = false
          it.percentage = 100
        })

        setUploadStage(clientId, 'done')
        clearUploadOp(clientId)
        return res
      }

      // -------------------------
      // VIDEO
      // -------------------------
      if (file.type.includes('video')) {
        let duration = 0
        let orientation: 'Landscape' | 'Portrait' | undefined

        setUploadStage(clientId, 'hash')
        const fileHash = await fastFileHash(file)
        throwIfUploadAborted(clientId)

        try {
          const meta = await import('helpers/video/readClientMeta').then(
            async m => await m.readClientVideoMeta(file)
          )
          duration = meta.duration || 0
          orientation = meta.orientation
        } catch {}

        throwIfUploadAborted(clientId)

        if (userData?.role !== 'fan') {
          setUploadStage(clientId, 'dupe')

          const ctx =
            selectedGroupId != null
              ? { group_ids: [selectedGroupId] }
              : { recipients: selectedRecipients ?? undefined }

          const dupe = await checkVideoDupe(
            {
              h: fileHash,
              size: file.size,
              ...ctx
            },
            signal
          )

          throwIfUploadAborted(clientId)

          if (dupe?.status === 'OK') {
            if (isMultiRecipientSend) {
              setHasDuplicateRecipients(true)
            }

            safeSyncById(it => {
              const d: any = dupe.data ?? {}

              it.type = 'video'
              it.processing = false
              it.percentage = 100
              it.error = ''

              if (!it.lockTouched) {
                it.locked = !!priceRef.current
              }

              it.storageUrl =
                d.storage_path ??
                d.path ??
                it.storageUrl ??
                null

              it.video_id =
                d.video_id ??
                d.id ??
                it.video_id ??
                null
              it.cloudfront =
                d.cloudfront ??
                it.cloudfront ??
                null

              it.customPreviewCloudfront =
                d.preview_cloudfront ??
                d.preview?.cloudfront ??
                it.customPreviewCloudfront ??
                null
              it.duration =
                d.duration ??
                duration ??
                it.duration

              it.orientation =
                d.original_orientation ??
                d.orientation ??
                orientation ??
                it.orientation

              it.exif_video_media =
                d.exif_media ??
                it.exif_video_media ??
                null

              it.duplicatePost = d.post

              it.hash_video =
                d.video_hash ??
                fileHash

              it.video_hash =
                it.hash_video

              const postVideo =
                d.post?.videos?.find(
                  (video: any) =>
                    video.id ===
                    (d.video_id ?? d.id)
                )

              const activeThumb =
                d.active_thumb ??
                postVideo?.active_thumb ??
                null

              if (Array.isArray(d.thumbnails)) {
                it.thumbnails =
                  d.thumbnails
              }

              if (
                typeof d.active_thumbnail ===
                'number'
              ) {
                it.activeThumbnailIndex =
                  d.active_thumbnail
              }

              if (activeThumb) {
                it.imagePreview =
                  activeThumb
              }

              it.custom_thumb_key =
                d.custom_thumb_key ??
                null

              it.preview_video_id =
                d.preview_video_id ??
                null

              it.customPreviewUrl =
                d.custom_preview_url ??
                null

              it.thumbsDone =
                !!activeThumb ||
                (
                  Array.isArray(it.thumbnails) &&
                  it.thumbnails.length > 0
                )

              it.thumbsQueryDone = true
            })

            const duplicateVideoId =
              dupe.data?.video_id ??
              dupe.data?.id

            if (
              typeof duplicateVideoId ===
              'string'
            ) {
              void getVideoThumbs([
                duplicateVideoId
              ])
                .then(response => {
                  const row =
                    response?.data?.data?.find(
                      (item: any) =>
                        item?.id ===
                        duplicateVideoId
                    )

                  if (!row) return

                  const thumbRows =
                    Array.isArray(row.thumbs)
                      ? row.thumbs
                      : []

                  const thumbnails =
                    thumbRows
                      .map(
                        (thumb: any) =>
                          thumb?.src
                      )
                      .filter(
                        (
                          src: unknown
                        ): src is string =>
                          typeof src ===
                            'string' &&
                          src.length > 0
                      )

                  const thumbnailTimes =
                    thumbRows.map(
                      (thumb: any) =>
                        thumb?.time != null &&
                        Number.isFinite(
                          Number(thumb.time)
                        )
                          ? Number(thumb.time)
                          : null
                    )

                  const activeThumbnailIndex =
                    thumbRows.findIndex(
                      (thumb: any) =>
                        thumb?.active === true
                    )

                  safeSyncById(it => {
                    it.thumbnails =
                      thumbnails

                    it.thumbnailTimes =
                      thumbnailTimes

                    it.activeThumbnailIndex =
                      activeThumbnailIndex >= 0
                        ? activeThumbnailIndex
                        : 0

                    it.storyboard =
                      row.storyboard ??
                      null

                    it.thumbsDone =
                      thumbnails.length > 0

                    it.thumbsQueryDone = true

                    it.thumbs_from =
                      'server'
                  })
                })
                .catch(error => {
                  console.warn(
                    '[CreateMessage] Failed to load duplicate video thumbs',
                    error
                  )
                })
            }

            setUploadStage(
              clientId,
              'done'
            )
            clearUploadOp(clientId)

            return null
          }
        }

        /*
         * Generate client thumbnails/scrub frames
         * only for a genuinely new video.
         *
         * This runs independently while the video
         * uploads to S3.
         */
        const clientThumbsPromise =
          generateClientThumbs(
            file,
            duration
          )
            .then(async generated => {
              if (isUploadAborted(clientId)) {
                revokeClientThumbs(
                  generated
                )

                return {
                  generated:
                    null as ClientThumbs | null,
                  valid: false,
                  error: null as unknown
                }
              }

              const expectedThumbCount =
                duration > 0
                  ? getExpectedClientThumbCount(duration)
                  : generated.thumbs.length

              const valid =
                await validateClientThumbs(
                  generated,
                  expectedThumbCount
                )

              if (isUploadAborted(clientId)) {
                revokeClientThumbs(
                  generated
                )

                return {
                  generated:
                    null as ClientThumbs | null,
                  valid: false,
                  error: null as unknown
                }
              }

              console.log(
                '[CreateMessage] Client thumbnail validation',
                {
                  valid,
                  expectedThumbCount,
                  thumbs:
                    generated.thumbs.length,
                  scrubFrames:
                    generated.scrubFrames.length
                }
              )

              if (!valid) {
                revokeClientThumbs(
                  generated
                )

                return {
                  generated:
                    null as ClientThumbs | null,
                  valid: false,
                  error: null as unknown
                }
              }

              safeSyncById(entry => {
                revokeClientThumbAssets(
                  entry
                )

                applyGeneratedClientThumbs(
                  entry,
                  generated
                )

                entry.processing = true
              })

              return {
                generated,
                valid: true,
                error: null as unknown
              }
            })
            .catch(error => {
              console.error(
                '[CreateMessage] Client thumbnail generation failed',
                error
              )

              return {
                generated:
                  null as ClientThumbs | null,
                valid: false,
                error
              }
            })

        setUploadStage(
          clientId,
          'presign'
        )

        const multipart =
          await startVideoMultipart(
            {
              type: 'post_video',
              contentType: file.type,
              size: file.size
            },
            signal
          )

        throwIfUploadAborted(clientId)

        const {
          key,
          uploadId,
          partSize,
          totalParts,
          parts
        } = multipart

        if (
          !Array.isArray(parts) ||
          parts.length !== totalParts
        ) {
          throw new Error(
            'Multipart setup did not return all presigned part URLs.'
          )
        }

        const uploadController =
          new AbortController()

        let multipartAborted = false
        let uploadError: any = null

        const cleanupMultipart =
          async () => {
            if (multipartAborted) return

            multipartAborted = true

            try {
              uploadController.abort()
            } catch {}

            await abortVideoMultipart({
              key,
              uploadId
            })
          }

        const handleOuterAbort = () => {
          void cleanupMultipart()
        }

        if (signal.aborted) {
          await cleanupMultipart()
          throwIfUploadAborted(clientId)
        }

        signal.addEventListener(
          'abort',
          handleOuterAbort,
          { once: true }
        )

        setUploadStage(
          clientId,
          'upload'
        )

        const completedParts =
          await uploadVideoMultipart({
            file,
            key,
            uploadId,
            partSize,
            totalParts,
            parts,
            contentType: file.type,
            concurrency: 4,
            signal:
              uploadController.signal,
            setProgress: (
              loaded,
              total
            ) => {
              const op =
                ensureUploadOp(
                  clientId
                )

              const cancelUpload = () => {
                cleanupMultipart()
                  .catch(() => {})
              }

              op.cancelCurrent =
                cancelUpload

              if (
                isUploadAborted(
                  clientId
                )
              ) {
                void cleanupMultipart()
                return
              }

              const cur = [
                ...(
                  progressInfosRef
                    .current?.val ??
                  []
                )
              ]

              const idx =
                findIndex(cur)

              if (idx < 0) return

              const it: any =
                cur[idx]

              it.percentage =
                Math.round(
                  (loaded * 100) /
                  (
                    total ||
                    file.size ||
                    1
                  )
                )

              it.cancel =
                cancelUpload

              it.error = ''
              it.type = 'video'

              it.previewOn =
                getPreviewStatus(
                  userData
                    .post_preview_type
                )

              it.processing = true

              syncProgress(
                cur as any
              )
            }
          })
          .catch(error => {
            if (
              uploadController
                .signal.aborted
            ) {
              return null
            }

            uploadError = error
            void cleanupMultipart()

            return null
          })

        signal.removeEventListener(
          'abort',
          handleOuterAbort
        )

        if (uploadError) {
          await cleanupMultipart()
          throw uploadError
        }

        throwIfUploadAborted(clientId)

        if (
          !completedParts?.length
        ) {
          await cleanupMultipart()

          throw new Error(
            'Multipart video upload did not complete.'
          )
        }

        try {
          await completeVideoMultipart(
            {
              key,
              uploadId,
              parts: completedParts
            },
            signal
          )
        } catch (error) {
          await cleanupMultipart()
          throw error
        }

        throwIfUploadAborted(clientId)

        const clientThumbs =
          await clientThumbsPromise

        throwIfUploadAborted(clientId)

        const clientThumbsReady =
          clientThumbs.valid &&
          clientThumbs.generated !== null

        setUploadStage(
          clientId,
          'attach'
        )

        const attach =
          await attachVideoFromS3(
            {
              s3_key: key,
              duration:
                duration > 0
                  ? Number(
                      duration.toFixed(2)
                    )
                  : null,
              orientation:
                orientation ?? null,
              video_hash:
                fileHash,
              preview:
                getPreviewStatus(
                  userData.post_preview_type
                ),
              client_thumbs_ready:
                clientThumbsReady,
              type: 'message'
            } as any,
            signal
          )

        throwIfUploadAborted(clientId)

        const resData =
          attach?.data?.data ??
          attach?.data ??
          attach

        safeSyncById(it => {
          const serverThumbnails =
            Array.isArray(
              resData?.thumbnails
            )
              ? resData.thumbnails
              : []

          it.storageUrl =
            resData?.path ??
            it.storageUrl ??
            key

          it.video_id =
            resData?.id ??
            it.video_id ??
            null

          it.duration =
            typeof resData?.duration ===
            'number'
              ? resData.duration
              : duration ||
                it.duration

          it.orientation =
            resData?.orientation ??
            orientation ??
            it.orientation

          if (!it.lockTouched) {
            it.locked =
              !!priceRef.current
          }

          it.processing = false
          it.percentage = 100

          it.hash_video =
            resData?.video_hash ??
            fileHash

          it.video_hash =
            it.hash_video

          if (!clientThumbsReady) {
            it.thumbnails =
              serverThumbnails

            it.activeThumbnailIndex =
              0

            it.imagePreview =
              serverThumbnails[0] ??
              it.imagePreview ??
              null

            it.thumbsDone =
              serverThumbnails.length > 0

            it.thumbsQueryDone = true

            it.thumbs_from =
              'server'
          }
        })

        setUploadStage(
          clientId,
          'done'
        )

        clearUploadOp(clientId)

        return attach
      }

      // -------------------------
      // AUDIO
      // -------------------------
      if (file.type.includes('audio')) {
        setUploadStage(clientId, 'upload')

        const data = new FormData()
        data.append('audio', file)

        const res = await attachAudio(
          data,
          (loaded: number, total: number, cancelCb: () => void) => {
            const op = ensureUploadOp(clientId)
            op.cancelCurrent = cancelCb

            if (isUploadAborted(clientId)) {
              try { cancelCb?.() } catch {}
              return
            }

            const cur = [...(progressInfosRef.current?.val ?? [])]
            const idx = findIndex(cur)
            if (idx >= 0) {
              const it: any = cur[idx]
              it.percentage = Math.round((100 * loaded) / (total || 1))
              it.cancel = cancelCb
              it.error = ''
              it.type = 'audio'
              it.processing = true
              if (!it.lockTouched) it.locked = !!priceRef.current
              syncProgress(cur as any)
            }
          },
          signal
        )
        throwIfUploadAborted(clientId)

        const resData = res?.data?.data
        if (resData?.storage_path) {
          safeSyncById((it) => {
            it.previewUrl = resData.path ?? it.previewUrl
            it.storageUrl = resData.storage_path
            if (!it.lockTouched) it.locked = !!priceRef.current
            it.processing = false
            it.percentage = 100
          })
        }

        setUploadStage(clientId, 'done')
        clearUploadOp(clientId)
        return res
      }

      clearUploadOp(clientId)
      return null
    } catch (err: any) {
      if (
        err?.code === '__UPLOAD_ABORTED__' ||
        err?.message === '__UPLOAD_ABORTED__' ||
        err?.name === 'AbortError' ||
        err?.code === 'ERR_CANCELED'
      ) {
        clearUploadOp(clientId)
        return null
      }

      const cur = [...(progressInfosRef.current?.val ?? [])]
      const idx = findIndex(cur)
      if (idx >= 0) {
        cur[idx].error = t('errorUploadingFile')
        cur[idx].processing = false
        syncProgress(cur as any)
      }

      clearUploadOp(clientId)
      throw new Error(err?.message || t('errorUploadingFile'))
    }
  },
  [t, userData.post_preview_type, isMultiRecipientSend]
)

const uploadFiles = (
  files: File[],
  selectedGroupId?: string | null,
  selectedRecipients?: string[] | null
) => {
  const fileIdMap = new Map<File, string>()

  const placeholders = files.map((file) => {
    const id = safeRandomUUID()
    fileIdMap.set(file, id)
    ensureUploadOp(id)

    return {
      id,
      file,
      size: file.size,
      percentage: 0,
      fileName: file.name,
      type: file.type.startsWith('video/')
        ? 'video'
        : file.type.startsWith('image/')
        ? 'photo'
        : file.type.startsWith('audio/')
        ? 'audio'
        : 'other',
      processing: true,
      previewOn: true,
      previewUrl: URL.createObjectURL(file),
      error: '',
      locked: !!priceRef.current
    } as any
  })

  const next = [...(progressInfosRef.current?.val ?? []), ...placeholders]
  syncProgress(next)

  files.forEach((file) => {
    const id = fileIdMap.get(file)
    void upload(file, selectedGroupId, selectedRecipients, id)
  })
}

const prepareMessage = (
  progInfos: IProgressInfo[],
  removeDuplicatesOverride?: boolean | null
) => {
  const byType = (type: 'photo' | 'video' | 'audio') =>
    progInfos.filter((i: IProgressInfo) => i.type === type)

  const photos = byType('photo')
    .filter(i => (i as any).photo_id)
    .map(item => ({
      id: (item as any).photo_id,
      path: item.storageUrl ?? null,

      // Backend attachment behavior
      is_duplicate:
        !!(item as any).is_duplicate,

      // FE-only optimistic chat display
      url: (item as any).url ?? undefined,
      thumb_url: (item as any).thumb_url ?? undefined,
      thumb_path: (item as any).thumb_path ?? undefined,

      order: progInfos.indexOf(item) + 1,
      orientation: item.orientation,
      locked:
        [...byType('photo'), ...byType('video')].length > 1
          ? item.locked
          : true,
      text: item.text,
      type: 'photo',
      exif_data: item.exif_data,
      hash_image: item.hash_image,
    }))

    const videos = byType('video')
      .filter(item =>
        typeof (item as any).video_id === 'string' &&
        (item as any).video_id.length > 0
      )
      .map(item => ({
        id: (item as any).video_id,
        order: progInfos.indexOf(item) + 1,
        duration:
          typeof item.duration === 'number'
            ? item.duration
            : null,
        orientation:
          item.orientation ?? null,
        locked:
          [...byType('video'), ...byType('photo')].length > 1
            ? !!item.locked
            : true,
        preview: !!item.previewOn,
        active_thumbnail:
          (item as any).activeThumbnailIndex ?? null,
        preview_thumb:
          (item as any).previewThumbIndex ?? null,
        preview_thumb_key:
          (item as any).previewThumbKey ?? null,
        custom_thumbnail:
          (item as any).custom_thumbnail ?? null,
        custom_thumb_key:
          (item as any).custom_thumb_key ?? null,
        custom_thumb_crop:
          (item as any).custom_thumb_crop ?? null,
        preview_video_id:
          (item as any).preview_video_id ?? null,
        video_clip:
          item.previewOn &&
          !(item as any).preview_video_id &&
          item.trimPreview
            ? {
                start: item.trimPreview.start,
                end: item.trimPreview.end
              }
            : null,
        video_hash:
          (item as any).hash_video ??
          (item as any).video_hash ??
          null,
        type: 'video'
      }))
      const sounds = byType('audio')
          .filter(i => i.storageUrl)
          .map(item => ({
              path: item.storageUrl,
              order: progInfos.indexOf(item) + 1,
              locked: item.locked,
              type: 'sound',
          }))

      const payload: any = {
          message: message.text,
          replied_message: reply?.message
              ? { message: reply.message }
              : null,
          replied_id: reply?.id ?? null,
          schedule_date:
              schedule !== ''
                  ? schedule
                  : null,
          schedule_timezone:
              schedule !== ''
                  ? Intl.DateTimeFormat().resolvedOptions().timeZone
                  : null,
          photos,
          videos,
          sounds,
          price: message.price,
          categories: selectedCategories.map(c => c.id),
      }

      if (isMultiRecipientSend) {
          payload.remove_duplicates =
              removeDuplicatesOverride ?? sendRemoveDuplicates
      }

      return payload
  }


    const renderAttachments = () => {
        return (
<div
    ref={attachmentsRef}
    className={`directmessage__attachments ${
        attachmentsOpen
            ? 'directmessage__attachments--active'
            : ''
    } ${
        attachmentsMinimized
            ? 'directmessage__attachments--minimized'
            : ''
    } ${
        isNewMessage
            ? 'directmessage__attachments--new-message'
            : ''
    } ${
        fanActionsOpen
            ? 'directmessage__attachments--fan-actions-open'
            : ''
    } ${customClass ?? ''} ${
        forceHideAttachments
            ? 'directmessage__attachments--inactive'
            : ''
    } ${
        message.vaultImages.length > 0
            ? 'directmessage__attachments--vault'
            : ''
    } ${
        message?.audioMessage && message?.audioPreview
            ? 'directmessage__attachments--sound'
            : ''
    }`}
>
                <div className='directmessage__attachments__header'>
                  <div
                      className="directmessage__attachments__title"
                      onClick={() => {
                          setAttachmentsMinimized(!attachmentsMinimized)
                      }}
                  >
                      <h1>Attached Files</h1>


                  </div>
                    {isCategoriesOpen && (
                        <Modal
                            onClose={closeCategoriesModal}
                            isLayerTransparent={false}
                        >
                            <SelectCategoriesModal
                                onCancel={closeCategoriesModal}
                                onSave={selected => {
                                    setSelectedCateories(selected)
                                    closeCategoriesModal()
                                }}
                                onlyOne={false}
                                selectedCategories={selectedCategories}
                            />
                        </Modal>
                    )}
                    {userData?.role !== 'fan' &&
                        (!message.price ? (
                            <RightAttachHeader>
                                <IconButton
                                    icon={<spriteIcons.IconList />}
                                    type='grey'
                                    clickFn={openCategoriesModal}
                                    customSizePx={35}
                                />{' '}
                                <Button
                                    text={
                                        !priceModalActive
                                            ? t('setPrice')
                                            : t('attachedFiles')
                                    }
                                    color='black'
                                    height='3'
                                    padding='2'
                                    font='mont-14-normal'
                                    width='fit'
                                    clickFn={() => {
                                        setPriceModalActive(!priceModalActive)
                                        setAttachmentsMinimized(false)
                                    }}
                                />
                            </RightAttachHeader>
                        ) : (
                            <RightAttachHeader>
                                <IconButton
                                    icon={<spriteIcons.IconList />}
                                    type='grey'
                                    clickFn={openCategoriesModal}
                                    customSizePx={35}
                                />
                                <div
                                    className='directmessage__attachments__pricebutton'
                                    onClick={() => {
                                        setPriceModalActive(!priceModalActive)
                                        setAttachmentsMinimized(false)
                                    }}
                                >
                                     ${formatPrice(message.price)} 
                                    <div className='directmessage__attachments__pricebutton__remove'>
                                        <img
                                            src={Icons.close}
                                            alt={t('removeFile')}
                                            onClick={(
                                                e: React.MouseEvent<HTMLElement>
                                            ) => {
                                                e.stopPropagation()
                                                setMessage(prev => ({ ...prev, price: 0 }));

                                                const cur = (progressInfosRef.current?.val ?? progressInfos.val) as any[];
                                                const next = cur.map(it => ({ ...it, locked: false })); // or keep existing lock rules
                                                syncProgress(next);
                                            }}
                                        />
                                    </div>
                                </div>
                            </RightAttachHeader>
                        ))}
                </div>
                {!priceModalActive ? (
<div className="attachments-dropzone" ref={attachmentsDropRef}>
  {sortableItems.map((item: IProgressInfo, key: number) => {
    const id = getId(item)

    if (item.type === 'photo') {
      return (
        <div
          key={id}
          className={`drop-card drop-card--image ${
            shouldShowDupeBorder(!!item.exif_media && !!item.previewUrl)
              ? 'uploadedfileBorder'
              : ''
          }`}
          data-id={id}
        >
          <PostImage
            isEdit={true}
            id={key}
            imagePreview={item.previewUrl}
            processing={!!item.processing}
            percentage={item.percentage}
            setPreviewOpen={setPreviewOpen}
            setPreviewMedia={(postId: string) => {
              setPreviewMedia({
                postId,
                post: (item as any).duplicatePost
              })
            }}
            mediaDta={
              shouldShowDupeBorder(!!item.exif_media && !!item.previewUrl)
                ? item.exif_media
                : null
            }
            abortUploadCb={() => {
              removeFile(key)
            }}
            uploadError={item.error}
            previewUrl={item.previewUrl}
            locked={item.locked}
            attachmentCount={sortableItems.length}
            isPremium={!!message.price}
            removeFile={() => {
              removeFile(key)
            }}
            removeByUrl={() => {
              removeFile(key)
            }}
            handleToggleMediaLocked={handleToggleMediaLocked}
            editPreview={() => {
              modalContext.addModal(
                '',
                <PhotoEditView
                  photo={{
                    url: item.previewUrl,
                    text: item.text
                  }}
                  handleClose={() => {
                    modalContext.clearModal()
                  }}
                  handleSetPhotoData={data => {
                    const cur = (progressInfosRef.current?.val ?? progressInfos.val) as any[]
                    const next = [...cur]
                    if (next[key]) next[key] = { ...next[key], text: data.text }
                    syncProgress(next as any)
                  }}
                />,
                true,
                true,
                'fullScreen no-close'
              )
            }}
          />

{item?.exif_media?.entity_type === 'Message' &&
item?.exif_media &&
item.previewUrl ? (
  <div className="exif_mediaRow">
    <div
      className="exif_mediaDate"
      onClick={(e) => {
        if (!isMultiRecipientSend) return

        e.stopPropagation()
        handleToggleDuplicateBehavior(
          `photo-${item.id ?? key}`,
          !(sendRemoveDuplicates === true)
        )
      }}
      style={{ cursor: isMultiRecipientSend ? 'pointer' : 'default' }}
    >
      {isMultiRecipientSend && (
        <>
          <spriteIcons.EyeIcon width="15" height="10" color="#ffffff" />
          <span>{item.exif_media.viewed}</span>
        </>
      )}

      {item.exif_media.updated_at && (
        <span className={isMultiRecipientSend ? 'exif_mediaDate__text' : ''}>
          Sent{' '}
          {(() => {
            const date = new Date(item.exif_media.updated_at)
            const now = new Date()
            const monthDay = date.toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric'
            })
            const isSameYear = date.getFullYear() === now.getFullYear()
            const year = isSameYear
              ? ''
              : ` '${String(date.getFullYear()).slice(-2)}`

            return `${monthDay}${year}`
          })()}
        </span>
      )}
    </div>

    {isMultiRecipientSend && (
      <span
        className={`exif_mediaActionToast ${
          duplicateActionToast?.key === `photo-${item.id ?? key}`
            ? 'exif_mediaActionToast--active'
            : ''
        }`}
      >
        {duplicateActionToast?.key === `photo-${item.id ?? key}`
          ? duplicateActionToast.text
          : ''}
      </span>
    )}
  </div>
) : item.exif_media?.entity_type === 'Post' &&
  item.exif_media &&
  item.previewUrl ? (
  <div className="exif_mediaRow">
    <div className="exif_mediaDate">
{(() => {
  const hasViews = (item.exif_media.viewed ?? 0) > 0

  const isGroupResult =
    Array.isArray(item.exif_media.group_ids) &&
    item.exif_media.group_ids.length > 0

  const shouldShowSent =
    !isMultiRecipientSend &&
    !isGroupResult &&
    hasViews

  const dateValue = shouldShowSent
    ? item.exif_media.updated_at
    : item.exif_media.created_at ?? item.exif_media.updated_at

  if (!dateValue) return null

  const date = new Date(dateValue)
  const now = new Date()

  const monthDay = date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric'
  })

  const isSameYear = date.getFullYear() === now.getFullYear()
  const year = isSameYear
    ? ''
    : ` '${String(date.getFullYear()).slice(-2)}`

  return (
    <span>
      {shouldShowSent ? 'Sent' : 'Posted'} {monthDay}
      {year}
    </span>
  )
})()}
    </div>
  </div>
) : null}
</div>
)
}

if (item.type === 'video') {
  return (
    <div
      key={id}
      className={`drop-card drop-card--video ${
        shouldShowDupeBorder(!!item.exif_video_media?.entity_type && !!item.previewUrl)
          ? 'uploadedfileBorder'
          : ''
      }`}
      data-id={id}
    >
      <PostVideo
        isEdit={true}
        id={key}
        processing={!!item.processing}
        thumbsReady={
          (item as any).thumbsDone === true ||
          item.fileName?.startsWith('shared-') ||
          (
            typeof (item as any).thumbTarget === 'number'
              ? Array.isArray(item.thumbnails) &&
                item.thumbnails.length >= (item as any).thumbTarget
              : Array.isArray(item.thumbnails) &&
                item.thumbnails.length >= 8
          )
        }
        setPreviewOpen={setPreviewOpen}
        setPreviewMedia={(postId: string) => {
          setPreviewMedia({
            postId,
            post: (item as any).duplicatePost
          })
        }}
        mediaDta={
          shouldShowDupeBorder(!!item.exif_video_media?.entity_type && !!item.previewUrl)
            ? item.exif_video_media
            : null
        }
        percentage={item.percentage}
        abortUploadCb={() => {
          removeFile(key)
        }}
        uploadError={item.error}
        previewUrl={item.previewUrl}
        imagePreview={item.imagePreview}
        locked={item.locked}
        isPremium={!!message.price}
        removeFile={() => {
          removeFile(key)
        }}
        removeByUrl={() => {
          removeFile(key)
        }}
        previewOn={item.previewOn}
        editPreview={() => {
          const currentItem =
            progressInfosRef.current?.val?.[key] ??
            progressInfos.val[key]

          if (!currentItem) return

          const resolvedThumbType:
            'clip' | 'thumb' =
              currentItem.preview_video_id
                ? 'clip'
                : 'thumb'

              handleEditVideoPreview(
                currentItem.previewUrl,
                String(currentItem.video_id),
                currentItem.storageUrl,
                currentItem.duration,
                currentItem.orientation,
                currentItem.thumbnails,
                currentItem.thumbnailTimes,
                currentItem.imagePreview,
                currentItem.trimPreview,
                resolvedThumbType,
                currentItem.customThumbnailUrl ?? null,
                currentItem.custom_thumbnail ?? null,
                currentItem.customPreviewUrl ?? null,
                currentItem.preview_video_id ?? null,
                currentItem.activeThumbnailIndex ?? null,
                currentItem.thumbsQueryDone ?? true,
                currentItem.scrubFrames ?? [],
                currentItem.previewThumbIndex ?? null,
                currentItem.previewThumbKey ?? null,
                currentItem.previewThumbUrl ?? null,
                currentItem.storyboard ?? null,
                currentItem.cloudfront ?? null,
                currentItem.customPreviewCloudfront ?? null
              )
        }}
        handleToggleMediaLocked={handleToggleMediaLocked}
        handleTogglePreviewOn={handleTogglePreviewOn}
      />

      {item?.exif_video_media?.entity_type === 'Message' &&
      item?.exif_video_media &&
      item.previewUrl ? (
        <div className="exif_mediaRow">
          <div
            className="exif_mediaDate"
            onClick={(e) => {
              if (!isMultiRecipientSend) return

              e.stopPropagation()
              handleToggleDuplicateBehavior(
                `video-${item.id ?? key}`,
                !(sendRemoveDuplicates === true)
              )
            }}
            style={{ cursor: isMultiRecipientSend ? 'pointer' : 'default' }}
          >
            {isMultiRecipientSend && (
              <>
                <spriteIcons.EyeIcon width="15" height="10" color="#ffffff" />
                <span>{item.exif_video_media.viewed}</span>
              </>
            )}

            {item.exif_video_media.updated_at && (
              <span className={isMultiRecipientSend ? 'exif_mediaDate__text' : ''}>
                Sent{' '}
                {(() => {
                  const date = new Date(item.exif_video_media.updated_at)
                  const now = new Date()
                  const monthDay = date.toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric'
                  })
                  const isSameYear = date.getFullYear() === now.getFullYear()
                  const year = isSameYear
                    ? ''
                    : ` '${String(date.getFullYear()).slice(-2)}`

                  return `${monthDay}${year}`
                })()}
              </span>
            )}
          </div>

          {isMultiRecipientSend && (
            <span
              className={`exif_mediaActionToast ${
                duplicateActionToast?.key === `video-${item.id ?? key}`
                  ? 'exif_mediaActionToast--active'
                  : ''
              }`}
            >
              {duplicateActionToast?.key === `video-${item.id ?? key}`
                ? duplicateActionToast.text
                : ''}
            </span>
          )}
        </div>
        ) : item.exif_video_media?.entity_type === 'Post' &&
          item.exif_video_media &&
          item.previewUrl ? (
          <div className='exif_mediaRow'>
            <div className='exif_mediaDate'>
              {(() => {
                const dateValue =
                  item.exif_video_media.created_at ??
                  item.exif_video_media.updated_at

                if (!dateValue) return null

                const date = new Date(dateValue)
                const now = new Date()

                const monthDay = date.toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric'
                })

                const isSameYear =
                  date.getFullYear() === now.getFullYear()

                const year = isSameYear
                  ? ''
                  : ` '${String(date.getFullYear()).slice(-2)}`

                return (
                  <span>
                    Posted {monthDay}
                    {year}
                  </span>
                )
              })()}
            </div>
          </div>
        ) : null}
            </div>
          )
        }

        return null
          })}

          {!!selectedFiles.length && (
            <div
              className="drop-card drop-card--image add-anything-card no-drag"
              data-no-drag="true"
              data-id="add-anything"
            >
              <AddAnything handleSetUpload={selectFiles} />
            </div>
          )}
        </div>
                ) : (
                <SetPrice
                  message={message}
                  setMessage={setMessage}
                  setPriceModalActive={setPriceModalActive}
                  progressInfos={progressInfos}
                  setProgressInfos={syncProgress}
                  
                />
                )}
            </div>
        )
    }

const handleScheduling = (val: Date | string) => {
    if (!val) {
        setSchedule('')
        return
    }

    let date: Date

    if (val instanceof Date) {
        date = val
    } else {
        date = new Date(val)
    }

    if (isNaN(date.getTime())) {
        setSchedule('')
        return
    }

    setSchedule(date.toISOString())
}

    useEffect(() => {
        if (setGifOpenInfo) {
            setGifOpenInfo(gifOpen)
        }
    }, [gifOpen])

    useEffect(() => {
        if (onMessageChange) {
            onMessageChange(message)
        }
    }, [message])

    return (
        <>
{hasGif && gifOpen && (
    <GifPopup
        closeFn={() => {
            setGifOpen(false)
        }}
        onGifClick={(gif: any) =>
            onGifClick ? onGifClick(gif) : null
        }
        bottomOffset="4rem"
    />
)}
            {editPreviewModalOpen && videoPreviewData && (
              <EditPreviewModal
                defaultPreview={
                  videoPreviewData.defaultPreview
                }
                cloudfront={videoPreviewData.cloudfront}
                customPreviewCloudfront={videoPreviewData.customPreviewCloudfront}
                storageSrc={
                  videoPreviewData.storageSrc
                }
                duration={
                  videoPreviewData.duration
                }
                orientation={
                  videoPreviewData.orientation
                }
                thumbnails={
                  videoPreviewData.thumbnails
                }
                thumbnailTimes={
                  videoPreviewData.thumbnailTimes
                }
                thumbnailIndex={
                  videoPreviewData.thumbnailIndex
                }
                previewThumbnailIndex={
                  videoPreviewData.previewThumbnailIndex
                }
                customPreviewThumbKey={
                  videoPreviewData.customPreviewThumbKey
                }
                customPreviewThumbnailUrl={
                  videoPreviewData.customPreviewThumbnailUrl
                }
                trimPreview={
                  videoPreviewData.trimPreview
                }
                thumbType={
                  videoPreviewData.thumbType
                }
                customThumbnailUrl={
                  videoPreviewData.customThumbnailUrl
                }
                customThumbnailTime={
                  videoPreviewData.customThumbnailTime
                }
                customPreviewUrl={
                  videoPreviewData.customPreviewUrl
                }
                previewVideoId={
                  videoPreviewData.previewVideoId
                }
                thumbsQueryDone={
                  videoPreviewData.thumbsQueryDone
                }
                scrubFrames={
                  videoPreviewData.scrubFrames
                }
                storyboard={
                  videoPreviewData.storyboard
                }
                uploadCustomThumbnail={async file =>
                  await uploadCustomVideoThumbnail(
                    file,
                    videoPreviewData.videoId
                  )
                }
                uploadCustomPreviewThumbnail={async file =>
                  uploadCustomPreviewThumbnail(
                    file,
                    videoPreviewData.videoId
                  )
                }
                uploadCustomPreview={
                  uploadCustomVideoPreview
                }
                cancelCb={() =>
                  setEditPreviewModalOpen(false)
                }
                confirmCb={
                  handleUpdatePreview
                }
              />
            )}
            {renderAttachments()}
            {
                <div
                    ref={replyRef}
                    className={`write-message-reply ${
                        reply ? 'write-message-reply-open' : ''
                    }`}
                >
                    <IconQuotes />
                    {typeof reply?.body === 'string' ? (
                        <p>{reply?.body}</p>
                    ) : (
                        reply && (
                            <AudioMessage
                                audioReady={true}
                                waveHeight={30}
                                timerTextSize={12}
                                timerTextColor={
                                    reply?.recieved ? 'gray' : 'white'
                                }
                                waveColor={reply?.recieved ? 'gray' : 'white'}
                                audioBlob={
                                    reply?.body.src ||
                                    reply?.body.url ||
                                    reply?.body.path ||
                                    ''
                                }
                                customClass={`${
                                    messageStyles.message_sound_player
                                } ${
                                    reply?.recieved
                                        ? messageStyles.message_sound_player_recieved
                                        : ''
                                } write-message-audio-reply`}
                            />
                        )
                    )}
                    <button
                        className='write-message-reply-close'
                        onClick={() => (setReply ? setReply(null) : null)}
                    >
                        <IconClose color='#ffffff' />
                    </button>
                </div>
            }
            {isChatCooldown &&
                createPortal(
                    <div className='chat-cooldown-popup'>
                        Chat On Cooldown..
                    </div>,
                    document.body
                )}

            <WriteMessage
                containerRef={composerRef}
                message={message}
                submitFn={submitFn}
                setMessage={setMessage}
                uploadMedia={uploadFiles}
                selectFiles={selectFiles}
                selectedFiles={selectedFiles}
                setSelectedFiles={setSelectedFiles}
                sendMessage={handleSendMessage}
                sending={sending ?? sendingLocal}
                sendingDisabled={sendingDisabled}
                customClass={writeMessageCustomClass}
                appendAttachments={appendAttachments}
                setProgressInfos={setProgressInfos}
                progressInfos={progressInfos}
                fanActionsOpen={fanActionsOpen}
                setFanActionsOpen={setFanActionsOpen}
                setGifOpen={() => {
                    setGifOpen(!gifOpen)
                }}
                hasGif={hasGif}
                removeFile={removeFile}
                onFocusIn={onFocusIn}
                onFocusOut={onFocusOut}
                setSchedule={handleScheduling}
                schedule={schedule}
                setReply={setReply}
                disabled={disabled}
                welcomeMessage={welcomeMessage}
                editWelcomeMessage={editWelcomeMessage}
                setMessageId={setMessageId}
                otherUserData={otherUserData}
                conversationId={conversationId}
                messagesReady={messagesReady}
                isChatCooldown={isChatCooldown}
            />
            {previewOpen &&
              previewMedia &&
              createPortal(
                <PostModal
                  postId={previewMedia.postId}
                  post={previewMedia.post}
                  newPostPage={true}
                  closeFn={() => {
                    setPreviewOpen(false)
                    setPreviewMedia(null)
                  }}
                />,
                document.body
              )}
        </>
    )
}

export default CreateMessage

const RightAttachHeader = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 1.4rem;
    align-items: center;
`
